/**
 * NÖBETÇİ (Faz 1) — sitenin başında bekleyen izleme merkezi.
 * Üç bölüm: durum kartları (uptime + kritik olaylar), olay akışı (Change Monitor +
 * Dizin Radarı + kesintiler, eski→yeni gösterimiyle), takip kodları özeti.
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useListDomains } from "@workspace/api-client-react";
import { useActiveDomain } from "@/hooks/use-active-domain";
import {
  ShieldAlert, Activity, AlertTriangle, Info, CheckCircle2, RefreshCw,
  ArrowRight, Radio, Tags, BellOff, Loader2, WifiOff,
} from "lucide-react";

const C = { green: "#12b76a", red: "#d92d20", amber: "#f79009", blue: "#2970ff", purple: "#7a5af8" };

type Olay = { id: number; pageId: number | null; url: string | null; tur: string; onem: string; eski: string | null; yeni: string | null; mesaj: string; okundu: boolean; createdAt: string };
type NobetciVeri = {
  olaylar: Olay[];
  sayilar: { okunmamis: number; kritik24: number; toplam7g: number };
  uptime: {
    yuzde24: number | null; yuzde7: number | null; yuzde30: number | null;
    sonKontrol: { ok: boolean; statusCode: number | null; ms: number | null; at: string } | null;
    acikKesintiBaslangici: string | null;
    sonKontroller: { ok: boolean; at: string; ms: number | null }[];
    kesintiGecmisi: Olay[];
  };
};
type KodOzeti = { taranmis: number; kodlar: { kod: string; sayfaSayisi: number; yaygin: boolean }[]; eksikSayfalar: { url: string; eksik: string[] }[]; hicYok: boolean };

const ONEM_STIL: Record<string, { renk: string; Ikon: typeof Info; ad: string }> = {
  kritik: { renk: C.red, Ikon: AlertTriangle, ad: "KRİTİK" },
  uyari: { renk: C.amber, Ikon: Info, ad: "uyarı" },
  bilgi: { renk: C.blue, Ikon: Info, ad: "bilgi" },
};

const kodAdi = (k: string) =>
  k.startsWith("GA4:") ? `Google Analytics · ${k.slice(4)}` :
  k.startsWith("GTM:") ? `Tag Manager · ${k.slice(4)}` :
  k.startsWith("ADS:") ? `Google Ads · ${k.slice(4)}` :
  k === "PIXEL" ? "Meta Pixel" : k;

const zaman = (iso: string) => {
  const d = new Date(iso), fark = Date.now() - d.getTime();
  if (fark < 3600_000) return `${Math.max(1, Math.round(fark / 60_000))} dk önce`;
  if (fark < 86_400_000) return `${Math.round(fark / 3600_000)} sa önce`;
  return d.toLocaleDateString("tr-TR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};

const yol = (u: string | null) => (u ? u.replace(/^https?:\/\/[^/]+/, "") || "/" : null);

export default function Nobetci() {
  const { data: domains } = useListDomains();
  const [activeDomainId] = useActiveDomain(domains);
  const qc = useQueryClient();
  const [filtre, setFiltre] = useState<"" | "kritik" | "uyari" | "bilgi">("");

  const { data: d, isLoading } = useQuery<NobetciVeri>({
    queryKey: ["nobetci", activeDomainId, filtre],
    enabled: !!activeDomainId,
    refetchInterval: 60_000,
    queryFn: async () => {
      const r = await fetch(`/api/domains/${activeDomainId}/nobetci${filtre ? `?onem=${filtre}` : ""}`);
      if (!r.ok) throw new Error("nobetci");
      return r.json();
    },
  });

  const { data: kodlar } = useQuery<KodOzeti>({
    queryKey: ["tracking-codes", activeDomainId],
    enabled: !!activeDomainId,
    queryFn: async () => {
      const r = await fetch(`/api/domains/${activeDomainId}/tracking-codes`);
      if (!r.ok) throw new Error("kod");
      return r.json();
    },
  });

  const okundu = useMutation({
    mutationFn: async () => { await fetch(`/api/domains/${activeDomainId}/nobetci/okundu`, { method: "POST", headers: { "content-type": "application/json" }, body: "{}" }); },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["nobetci"] }); void qc.invalidateQueries({ queryKey: ["nobetci-zil"] }); },
  });

  const kontrol = useMutation({
    mutationFn: async () => {
      const r = await fetch(`/api/domains/${activeDomainId}/uptime/check`, { method: "POST" });
      return r.json() as Promise<{ ok: boolean; statusCode: number | null; ms: number }>;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["nobetci"] }),
  });

  const { data: cwv } = useQuery<{ anahtarVar: boolean; mobil: { lcpMs: number | null; inpMs: number | null; clsX1000: number | null; perfSkor: number | null; alanVerisi: boolean; fetchedAt: string } | null; masaustu: { lcpMs: number | null; inpMs: number | null; clsX1000: number | null; perfSkor: number | null; alanVerisi: boolean; fetchedAt: string } | null }>({
    queryKey: ["cwv", activeDomainId],
    enabled: !!activeDomainId,
    queryFn: async () => (await fetch(`/api/domains/${activeDomainId}/cwv`)).json(),
  });
  const cwvOlc = useMutation({
    mutationFn: async () => {
      const r = await fetch(`/api/domains/${activeDomainId}/cwv/olc`, { method: "POST" });
      if (!r.ok) throw new Error(((await r.json().catch(() => ({}))) as { error?: string }).error ?? "Ölçüm başarısız");
      return r.json();
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["cwv"] }),
  });

  const u = d?.uptime;
  const kesintide = !!u?.acikKesintiBaslangici;

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      {/* Başlık */}
      <div className="mb-5 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: `${kesintide ? C.red : C.green}18` }}>
          <ShieldAlert className="h-6 w-6" style={{ color: kesintide ? C.red : C.green }} />
        </div>
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">Nöbetçi
            <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold" style={{ background: `${kesintide ? C.red : C.green}18`, color: kesintide ? C.red : C.green }}>
              {kesintide ? "● kesinti" : "● nöbette"}
            </span>
          </h1>
          <p className="text-sm text-muted-foreground">Kesinti, bozulan etiket, dizinden düşen sayfa — sitede ne değişirse burada ve zilde.</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {(d?.sayilar.okunmamis ?? 0) > 0 && (
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => okundu.mutate()}><BellOff className="h-3.5 w-3.5" /> Tümünü okundu say ({d!.sayilar.okunmamis})</Button>
          )}
          <Button size="sm" variant="outline" className="gap-1.5" disabled={kontrol.isPending} onClick={() => kontrol.mutate()}>
            {kontrol.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Şimdi kontrol et
          </Button>
        </div>
      </div>

      {kesintide && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border px-4 py-3 text-sm font-medium" style={{ borderColor: `${C.red}55`, background: `${C.red}0d`, color: C.red }}>
          <WifiOff className="h-4 w-4 shrink-0" /> Site şu anda erişilemiyor görünüyor — kesinti {zaman(u!.acikKesintiBaslangici!)} başladı. Toparlanınca burada ve zilde göreceksin.
        </div>
      )}

      {/* Durum kartları */}
      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          { ad: "Erişilebilirlik (24 saat)", deger: u?.yuzde24 != null ? `%${u.yuzde24}` : "—", renk: (u?.yuzde24 ?? 100) < 99 ? C.amber : C.green, Ikon: Activity, alt: u?.sonKontrol ? `son: ${u.sonKontrol.ok ? `${u.sonKontrol.statusCode ?? "OK"} · ${u.sonKontrol.ms ?? "?"}ms` : "BAŞARISIZ"}` : "henüz kontrol yok" },
          { ad: "Erişilebilirlik (30 gün)", deger: u?.yuzde30 != null ? `%${u.yuzde30}` : "—", renk: (u?.yuzde30 ?? 100) < 99.5 ? C.amber : C.green, Ikon: Radio, alt: `7 gün: ${u?.yuzde7 != null ? `%${u.yuzde7}` : "—"}` },
          { ad: "Kritik olay (24 saat)", deger: String(d?.sayilar.kritik24 ?? 0), renk: (d?.sayilar.kritik24 ?? 0) > 0 ? C.red : C.green, Ikon: AlertTriangle, alt: `7 günde ${d?.sayilar.toplam7g ?? 0} olay` },
          { ad: "Okunmamış bildirim", deger: String(d?.sayilar.okunmamis ?? 0), renk: (d?.sayilar.okunmamis ?? 0) > 0 ? C.blue : C.green, Ikon: Info, alt: "zil ile senkron" },
        ].map((k) => (
          <Card key={k.ad}><CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{k.ad}</span>
              <k.Ikon className="h-4 w-4" style={{ color: k.renk }} />
            </div>
            <div className="mt-1 text-2xl font-bold tabular-nums" style={{ color: k.renk }}>{k.deger}</div>
            <div className="mt-0.5 text-[11px] text-muted-foreground">{k.alt}</div>
          </CardContent></Card>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Olay akışı */}
        <Card className="lg:col-span-2"><CardContent className="p-4">
          <div className="mb-3 flex items-center gap-2">
            <span className="text-sm font-semibold">Değişiklik Günlüğü</span>
            <span className="text-[11px] text-muted-foreground">— iki tarama arası farklar + kesintiler + dizin olayları</span>
            <div className="ml-auto flex gap-1">
              {([["", "Tümü"], ["kritik", "Kritik"], ["uyari", "Uyarı"], ["bilgi", "Bilgi"]] as const).map(([f, ad]) => (
                <button key={f} onClick={() => setFiltre(f)}
                  className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition ${filtre === f ? "bg-brand-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-white/[0.06] dark:text-gray-300"}`}>
                  {ad}
                </button>
              ))}
            </div>
          </div>
          {isLoading ? (
            <div className="py-10 text-center text-sm text-muted-foreground"><Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />Yükleniyor…</div>
          ) : !d || d.olaylar.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              <CheckCircle2 className="mx-auto mb-2 h-6 w-6" style={{ color: C.green }} />
              Kayda değer bir değişiklik yok — Nöbetçi izlemeye devam ediyor.<br />
              <span className="text-[11px]">Olaylar her taramada (fark), 5 dakikada bir (uptime) ve her gece (dizin radarı) üretilir.</span>
            </div>
          ) : (
            <div className="max-h-[560px] space-y-2 overflow-y-auto pr-1">
              {d.olaylar.map((o) => {
                const s = ONEM_STIL[o.onem] ?? ONEM_STIL.bilgi!;
                return (
                  <div key={o.id} className={`rounded-lg border p-3 text-sm ${o.okundu ? "opacity-70" : ""}`} style={{ borderColor: `${s.renk}33`, background: o.okundu ? "transparent" : `${s.renk}08` }}>
                    <div className="flex items-start gap-2">
                      <s.Ikon className="mt-0.5 h-4 w-4 shrink-0" style={{ color: s.renk }} />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-baseline gap-x-2">
                          <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: s.renk }}>{s.ad}</span>
                          <span className="text-[11px] text-muted-foreground">{zaman(o.createdAt)}</span>
                          {yol(o.url) && <span className="truncate text-[11px] text-muted-foreground" title={o.url ?? ""}>{yol(o.url)}</span>}
                        </div>
                        <div className="mt-0.5">{o.mesaj}</div>
                        {(o.eski || o.yeni) && (
                          <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[12px]">
                            {o.eski && <span className="rounded bg-gray-100 px-1.5 py-0.5 line-through decoration-gray-400 dark:bg-white/[0.06]" title="önceki">{o.eski}</span>}
                            {o.eski && o.yeni && <ArrowRight className="h-3 w-3 text-muted-foreground" />}
                            {o.yeni && <span className="rounded px-1.5 py-0.5" style={{ background: `${s.renk}14`, color: s.renk }} title="yeni">{o.yeni}</span>}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent></Card>

        {/* Sağ kolon: uptime geçmişi + takip kodları */}
        <div className="space-y-4">
          <Card><CardContent className="p-4">
            <div className="mb-2 flex items-center gap-2"><Activity className="h-4 w-4" style={{ color: C.green }} /><span className="text-sm font-semibold">Son Kontroller</span><span className="text-[11px] text-muted-foreground">5 dk arayla</span></div>
            {u && u.sonKontroller.length > 0 ? (
              <>
                <div className="flex h-8 items-end gap-[2px]">
                  {u.sonKontroller.map((k, i) => (
                    <div key={i} className="flex-1 rounded-sm" title={`${new Date(k.at).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })} — ${k.ok ? `OK · ${k.ms ?? "?"}ms` : "BAŞARISIZ"}`}
                      style={{ height: k.ok ? `${Math.min(100, Math.max(30, 100 - (k.ms ?? 0) / 40))}%` : "100%", background: k.ok ? C.green : C.red, opacity: k.ok ? 0.75 : 1 }} />
                  ))}
                </div>
                <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
                  <span>{u.sonKontroller[0] ? zaman(u.sonKontroller[0].at) : ""}</span><span>şimdi</span>
                </div>
              </>
            ) : (
              <p className="text-[12px] text-muted-foreground">Henüz kontrol kaydı yok — "Şimdi kontrol et" ile başlat; sunucuda 5 dakikada bir otomatik koşar.</p>
            )}
            {u && u.kesintiGecmisi.filter((k) => k.tur === "kesinti").length > 0 && (
              <div className="mt-3 border-t pt-2 text-[12px]">
                <div className="mb-1 font-medium text-muted-foreground">Kesinti geçmişi</div>
                {u.kesintiGecmisi.filter((k) => k.tur === "kesinti").slice(0, 4).map((k) => (
                  <div key={k.id} className="flex items-center gap-1.5 py-0.5"><WifiOff className="h-3 w-3" style={{ color: C.red }} />{zaman(k.createdAt)}</div>
                ))}
              </div>
            )}
          </CardContent></Card>

          <Card><CardContent className="p-4">
            <div className="mb-2 flex items-center gap-2"><Activity className="h-4 w-4" style={{ color: C.blue }} /><span className="text-sm font-semibold">Core Web Vitals</span>
              <button onClick={() => cwvOlc.mutate()} disabled={cwvOlc.isPending} className="ml-auto flex items-center gap-1 text-[11.5px] font-medium text-brand-600 hover:underline disabled:opacity-50">
                {cwvOlc.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Şimdi ölç
              </button>
            </div>
            {cwvOlc.isError && <p className="mb-2 text-[11.5px]" style={{ color: C.red }}>{(cwvOlc.error as Error).message}</p>}
            {!cwv?.mobil && !cwv?.masaustu ? (
              <p className="text-[12px] text-muted-foreground">Henüz ölçüm yok — "Şimdi ölç" ~1 dk sürer (Google PageSpeed).{!cwv?.anahtarVar && " Gecelik otomatik ölçüm için .env'e ücretsiz PAGESPEED_API_KEY eklenmeli."}</p>
            ) : (
              <div className="space-y-2">
                {([["Mobil", cwv?.mobil], ["Masaüstü", cwv?.masaustu]] as const).map(([ad, m]) => m && (
                  <div key={ad} className="text-[12.5px]">
                    <div className="mb-0.5 flex items-center justify-between"><span className="font-medium">{ad}</span>{m.perfSkor != null && <span className="tabular-nums font-semibold" style={{ color: m.perfSkor >= 90 ? C.green : m.perfSkor >= 50 ? C.amber : C.red }}>{m.perfSkor}/100</span>}</div>
                    {m.alanVerisi ? (
                      <div className="flex gap-2 text-[11.5px] tabular-nums">
                        <span style={{ color: (m.lcpMs ?? 0) <= 2500 ? C.green : (m.lcpMs ?? 0) <= 4000 ? C.amber : C.red }}>LCP {m.lcpMs != null ? (m.lcpMs / 1000).toFixed(1) + "s" : "—"}</span>
                        <span style={{ color: (m.inpMs ?? 0) <= 200 ? C.green : (m.inpMs ?? 0) <= 500 ? C.amber : C.red }}>INP {m.inpMs ?? "—"}ms</span>
                        <span style={{ color: (m.clsX1000 ?? 0) <= 100 ? C.green : (m.clsX1000 ?? 0) <= 250 ? C.amber : C.red }}>CLS {m.clsX1000 != null ? (m.clsX1000 / 1000).toFixed(2) : "—"}</span>
                      </div>
                    ) : <div className="text-[11px] text-muted-foreground">Gerçek kullanıcı (CrUX) örneklemi yok — laboratuvar skoru gösteriliyor.</div>}
                  </div>
                ))}
              </div>
            )}
          </CardContent></Card>

          <Card><CardContent className="p-4">
            <div className="mb-2 flex items-center gap-2"><Tags className="h-4 w-4" style={{ color: C.purple }} /><span className="text-sm font-semibold">Takip Kodları</span></div>
            {!kodlar || kodlar.taranmis === 0 ? (
              <p className="text-[12px] text-muted-foreground">Kod tespiti için siteyi bir kez (yeniden) taramak gerekiyor — tarama sırasında GA4/GTM/Pixel/Ads etiketleri sayfa sayfa okunur.</p>
            ) : kodlar.hicYok ? (
              <div className="flex items-start gap-2 text-[13px]" style={{ color: C.red }}>
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Taranan {kodlar.taranmis} sayfanın HİÇBİRİNDE takip kodu yok — Google Analytics kurulu değilse trafiğin tamamen ölçümsüz.</span>
              </div>
            ) : (
              <>
                <div className="space-y-1.5">
                  {kodlar.kodlar.map((k) => (
                    <div key={k.kod} className="flex items-center justify-between text-[12.5px]">
                      <span className="flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5" style={{ color: C.green }} />{kodAdi(k.kod)}</span>
                      <span className="tabular-nums text-muted-foreground">{k.sayfaSayisi}/{kodlar.taranmis} sayfa</span>
                    </div>
                  ))}
                </div>
                {kodlar.eksikSayfalar.length > 0 && (
                  <div className="mt-3 border-t pt-2">
                    <div className="mb-1 flex items-center gap-1.5 text-[12px] font-medium" style={{ color: C.amber }}>
                      <AlertTriangle className="h-3.5 w-3.5" /> {kodlar.eksikSayfalar.length} sayfada eksik kod
                    </div>
                    <div className="max-h-36 space-y-0.5 overflow-y-auto text-[11.5px] text-muted-foreground">
                      {kodlar.eksikSayfalar.slice(0, 12).map((p) => (
                        <div key={p.url} className="truncate" title={`${p.url} — eksik: ${p.eksik.join(", ")}`}>{yol(p.url)}</div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent></Card>
        </div>
      </div>
    </div>
  );
}
